import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiResponse } from '../../shared/models/api-response.model';

/**
 * Abstract base service for HTTP communication with the Koha REST API.
 *
 * Provides common methods for GET requests with pagination support.
 * Feature-specific services extend this class and set their own `basePath`.
 *
 * All Koha pagination responses include total count in the `X-Total-Count`
 * response header, which this base class extracts automatically.
 */
@Injectable()
export abstract class BaseApiService {
  protected http = inject(HttpClient);

  /** Base API path — set by subclasses (e.g., '/api/v1') */
  protected abstract basePath: string;

  /**
   * Simple GET request returning typed response body.
   */
  protected get<T>(path: string, params?: HttpParams, headers?: { [header: string]: string | string[] }): Observable<T> {
    return this.http.get<T>(`${this.basePath}${path}`, { params, headers });
  }

  protected post<T>(path: string, body: any, params?: HttpParams, headers?: { [header: string]: string | string[] }): Observable<T> {
    return this.http.post<T>(`${this.basePath}${path}`, body, { params, headers });
  }

  /**
   * Simple DELETE request.
   */
  protected delete<T>(path: string, params?: HttpParams, headers?: { [header: string]: string | string[] }): Observable<T> {
    return this.http.delete<T>(`${this.basePath}${path}`, { params, headers });
  }

  /**
   * GET request that returns both the response body and the total count
   * from Koha's `X-Total-Count` header. Used for all paginated endpoints.
   *
   * @param path - API path relative to basePath
   * @param params - HTTP query parameters
   * @param headers - Optional HTTP headers
   * @returns Observable of `ApiResponse<T>` with data and totalCount
   */
  protected getWithCount<T>(path: string, params?: HttpParams, headers?: { [header: string]: string | string[] }): Observable<ApiResponse<T>> {
    return this.http.get<T>(`${this.basePath}${path}`, {
      params,
      headers,
      observe: 'response',
    }).pipe(
      map(response => ({
        data: (response.body ?? []) as T,
        totalCount: +(response.headers.get('X-Total-Count') ?? '0'),
      }))
    );
  }
}
